/* React */
import { useEffect, useState } from 'react'

/* Redux */
import { useSelector } from 'src/redux/hooks'

/* Material */
import useStyles from './styles.Carousel'

/* Types */
import { tProduct } from 'src/shared/frontendTypes/product'
import { Images, imageSrcs } from 'src/shared/frontendTypes/images'

/* Functions */
import getImageUrl from '../../functions/imageUrl'
import getImageAlt from '../../functions/imageAlt'
import config from 'src/config'

/* Components */
import PreviewModal from './PreviewModal'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import noImage from 'src/images/camera.svg'

/* Swiper */
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, {
  Navigation,
  Thumbs,
  Zoom,
} from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'

SwiperCore.use([
  Navigation,
  Thumbs,
  Zoom,
])

const defaultSize = {
  width: 450,
  height: 450,
}

const ImagesCarousel: React.FC<{
  images: Images | imageSrcs
  product: tProduct
  mainImageHeight?: number
  mainImageSize?: 'xs' | 'sm' | 'md' | 'lg'
  disableImageZoomModal?: boolean
  disableTouchSwipe?: boolean
}> = ({
  images,
  product,
  mainImageHeight,
  mainImageSize = 'md',
  disableImageZoomModal = false,
  disableTouchSwipe = false,
}) => {
  const classes = useStyles()
  const { id: productID, metaTitle } = product
  const fullscreenImageViewer = config.productImageViewer?.variant === 'fullscreen'

  const touchDevice = useSelector(store => store.App.isTouchDevice)
  const [showModal, toggleModal] = useState(false)
  const [imageIndex, setImageIndex] = useState<number>(0)

  const [mainSwiper, setMainSwiper] = useState<SwiperCore>()
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>()
  const firstImage = images[0]
  const firstImageKey = firstImage
    ? [
      'src' in firstImage ? firstImage.src : '',
      firstImage.xs,
      firstImage.sm,
      firstImage.md,
      firstImage.lg,
      firstImage.xl,
      firstImage.xxl,
    ].join('|')
    : ''

  useEffect(() => {
    setImageIndex(0)
    mainSwiper?.slideTo(0)
    thumbsSwiper?.slideTo(0)
  }, [firstImageKey, mainSwiper, thumbsSwiper])

  if (images.length === 0)
    return (
      <div className={classes.carouselWrapper} style={{ padding: 50 }}>
        <img
          alt="Kein Bild gefunden"
          src={noImage.src}
          height={defaultSize.height}
          width={defaultSize.width}
          loading="eager"
        />
      </div>
    )

  return (
    <div className={classes.carouselWrapper}>
      {/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */}
      {/* Das große Bild */}

      <Swiper
        className={[
          classes.imageSwiper,
          fullscreenImageViewer ? classes.imageSwiperModalOnly : '',
        ].filter(Boolean).join(' ')}
        style={mainImageHeight ? { height: mainImageHeight } : undefined}
        spaceBetween={50}
        slidesPerView={1}
        onSwiper={setMainSwiper}
        simulateTouch={!disableTouchSwipe}
        allowTouchMove={!disableTouchSwipe}
        cssMode={false}
        zoom={!fullscreenImageViewer && !disableImageZoomModal} // Ist automatisch nur für Touch-Devices
        navigation={false}
        thumbs={{
          swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
        }}
        onSlideChange={swiper => setImageIndex(swiper.activeIndex)}
      >
        {images.map((image, index) => {
          const imagePath = getImageUrl(image, productID, mainImageSize)
          const alt = getImageAlt(image, metaTitle)
          const imgRender = (
            <img
              alt={alt}
              src={imagePath || noImage.src}
              height={defaultSize.height}
              width={defaultSize.width}
              loading={index === 0 ? 'eager' : 'lazy'}
              {...{ fetchpriority: index === 0 ? 'high' : 'auto' }}
            />
          )

          return (
            <SwiperSlide
              key={index}
              className={classes.imageSlide}
              onClick={() => {
                if (disableImageZoomModal) return
                const imageUrl = getImageUrl(image, productID, 'lg')
                if (imageUrl) {
                  setImageIndex(index)
                  toggleModal(true)
                }
              }}
            >
              <div className="swiper-zoom-container">{imgRender}</div>
              <AiGeneratedBadge aiGenerated={image.aiGenerated} />
            </SwiperSlide>
          )
        })}
      </Swiper>

      {/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */}
      {/* Die Thumbnail-Navigation */}

      <Swiper
        className={classes.thumbs}
        spaceBetween={10}
        slidesPerView={'auto'}
        simulateTouch={!disableTouchSwipe} // BugFix für Desktop, sonst funktioniert die Navigation nicht.
        allowTouchMove={!disableTouchSwipe}
        navigation={!touchDevice}
        cssMode={false}
        watchSlidesProgress
        slideToClickedSlide
        onSwiper={setThumbsSwiper}
      >
        {images.map((image, index) => {
          const imagePath = getImageUrl(image, productID, 'xs')
          const alt = getImageAlt(image, metaTitle)

          return (
            <SwiperSlide
              key={index}
              className={classes.thumbnailSlide}
              onClick={() => {
                setImageIndex(index)
                mainSwiper?.slideTo(index)
              }}
            >
              <img
                alt={alt}
                src={imagePath || noImage.src}
                height={100}
                width={100}
                loading="lazy"
                decoding="async"
                {...{ fetchpriority: 'low' }}
              />
              <AiGeneratedBadge aiGenerated={image.aiGenerated} size='compact' />
            </SwiperSlide>
          )
        })}
      </Swiper>

      {/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */}

      {!disableImageZoomModal && (
        <PreviewModal
          {...{
            images,
            imageIndex,
            productID,
            metaTitle,
            fullscreen: fullscreenImageViewer,
          }}
          open={showModal}
          handleClose={() => {
            toggleModal(false)
          }}
        />
      )}
    </div>
  )
}

export default ImagesCarousel
